<template>
  <div class="block sm:inline-block mb-3 mr-3">
    <div
      class="relative grid grid-cols-[39px_80px_1fr] px-1 gap-1 w-full sm:w-80 h-12 items-center overflow-hidden rounded-md cursor-pointer transition-all duration-200 group"
      :class="{
        'bg-slate-700': isInputRouted,
        'bg-slate-800': !isInputRouted,
        'ring-2 ring-blue-400': selectedInput === input,
        'ring-2 ring-orange-400 connected': selectedInput === input,
        'ring-2 ring-orange-400 connected': activeOutputInput.id === input.id,
        'opacity-50 pointer-events-none': !activeOutputInput && activeOutputInput.id !== input.id,
      }"
      @click="$emit('route-input', input, selectedOutputs)"
    >
      <!-- Loading Overlay - Show during routing operations -->
      <div 
        v-if="isRoutingLoading"
        class="absolute left-0 right-0 top-0 bottom-0 bg-slate-700/90 flex items-center justify-center z-40 rounded-sm backdrop-blur-xs routing-loading pointer-events-none">
        <div class="flex items-center space-x-2 text-blue-400">
          <ma-loading class="bly-loading animate-spin text-lg"></ma-loading>
          <span class="text-sm font-medium">Routing...</span>
        </div>
      </div>

      <!-- Avatar -->
      <div class="flex justify-center pl-0 -ml-1">
        <el-avatar
          shape="square"
          size="medium"
          :style="{ background: inputColor }"
          :title="input.id"
        >
          {{ index + 1 }}
        </el-avatar>
      </div>

      <!-- Thumbnail Preview -->
      <div class="relative h-10 w-18 -ml-1 flex items-center justify-center bg-slate-900/50 rounded-r overflow-hidden thumbnail-preview">
        <!-- Thumbnail Image -->
        <img
          v-if="thumbnailData.data && !thumbnailData.error && !thumbnailData.warning"
          :src="thumbnailData.data || placeholder"
          :alt="`${input.name} preview`"
          class="w-full h-full object-cover"
          :title="`Last updated: ${formatThumbnailTime(thumbnailData.timestamp)}`"
        />
        
        <!-- Loading State -->
        <div v-else-if="thumbnailData.loading" class="flex items-center justify-center text-blue-400 thumbnail-loading">
          <ma-loading class="bly-loading animate-spin text-xs"></ma-loading>
        </div>

        <!-- Warning State -->
        <div v-else-if="thumbnailData.warning" class="flex items-center justify-center text-yellow-400 thumbnail-warning">
          <el-tooltip :content="thumbnailData.warning" placement="top">
            <ma-warning class="bly-warning text-xs"></ma-warning>
          </el-tooltip>
        </div>
        
        <!-- Error State -->
        <div v-else-if="thumbnailData.error" class="flex items-center justify-center text-red-400 thumbnail-error" :title="thumbnailData.error">
          <i class="bi bly-alert text-xs"></i>
        </div>
        
        <!-- No Data State -->
        <div v-else class="flex items-center justify-center text-slate-500 thumbnail-no-data">
          <ma-image class="bly-image text-xs"></ma-image>
        </div>
      </div>

      <!-- Content -->
      <div class="min-w-0 relative">
        <div class="text-white text-sm font-medium truncate">
          {{ input.name }}
        </div>
        <div class="text-xs leading-4 mt-0.5">
          <div v-if="!connectedOutputs.length" class="text-red-500 opacity-0 hidden">
            not routed
          </div>
          <div v-else>
            <el-tooltip>
              <div slot="content">
                <div
                  v-for="output in connectedOutputs"
                  :key="output.id"
                  class="text-blue-400"
                >
                  {{ output.name }}
                </div>
              </div>
              <div>
                <span class="text-blue-400 mr-1">
                  {{ connectedOutputs[0]?.name }}
                </span>
                <span v-show="connectedOutputs.length > 1" class="text-blue-400">
                  ... ({{ connectedOutputs.length }})
                </span>
              </div>
            </el-tooltip>
          </div>
        </div>

        <!-- Input Action Controls - Hover Overlay -->
        <div
          class="absolute top-0 right-0 h-full flex items-center px-2 bg-linear-to-l from-slate-800 via-slate-800/95 to-transparent opacity-0 invisible group-hover:opacity-100 group-hover:visible touch:opacity-100 touch:visible transition-all duration-300 ease-in-out transform translate-x-2 group-hover:translate-x-0 touch:translate-x-0 z-30"
          v-if="hasPermissions && !isRoutingLoading"
        >
          <div class="flex items-center space-x-2">
            <!-- TAKE Action - Only visible when outputs are selected -->
            <div v-if="!showTakeConfirmation && selectedOutputs && selectedOutputs.length > 0">
              <button
                size="mini"
                type="danger"
                @click.stop="showTakeConfirmation = true"
                class="opacity-90 hover:opacity-100 transition-opacity duration-150 bg-red-600 focus:outline-hidden focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 px-3 min-w-[90px] text-white! cursor-pointer rounded-md text-xs h-[26px]"
                :aria-label="`Route ${input.name} to selected outputs`"
                :title="`Route ${input.name} to selected outputs - Click to confirm`"
                tabindex="0"
              >
                TAKE
              </button>
            </div>

            <!-- TAKE Confirmation Buttons -->
            <div v-else-if="showTakeConfirmation && selectedOutputs && selectedOutputs.length > 0" class="flex items-center space-x-1">
              <button
                @click.stop="confirmTake"
                class="opacity-90 hover:opacity-100 transition-opacity duration-150 bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 px-2 text-white cursor-pointer rounded-md text-xs h-[26px]"
                title="Confirm TAKE action"
                tabindex="0"
              >
                CONFIRM
              </button>
              <button
                @click.stop="cancelTake"
                class="opacity-90 hover:opacity-100 transition-opacity duration-150 bg-slate-600 focus:outline-hidden focus:ring-2 focus:ring-slate-400 focus:ring-opacity-50 px-2 text-white cursor-pointer rounded-md text-xs h-[26px]"
                title="Cancel TAKE action"
                tabindex="0"
              >
                CANCEL
              </button>
            </div>

            <!-- Actions Menu (Ellipsis) -->
            <el-dropdown 
              @command="handleMenuCommand"
              trigger="click"
              placement="bottom-end"
              v-if="isSysAdmin"
              @click.stop
            >
              <el-button
                size="mini"
                type="info"
                icon="el-icon-more"
                @click.stop
                class="opacity-90 hover:opacity-100 transition-opacity duration-150 focus:outline-hidden focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
                :aria-label="`More actions for ${input.name}`"
                :title="`More actions for ${input.name}`"
                tabindex="0"
                circle
              >
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <template v-if="input.type === 'MEDIA_CONNECT' || input.type === 'ROUTE'">
                  <el-dropdown-item command="startInput" icon="el-icon-video-play">
                    Start Input
                  </el-dropdown-item>
                  <el-dropdown-item command="stopInput" icon="el-icon-video-pause">
                    Stop Input
                  </el-dropdown-item>
                  <el-dropdown-item command="restartInput" icon="el-icon-refresh-right">
                    Restart Input
                  </el-dropdown-item>
                </template>
                <el-dropdown-item command="restartEncoder" icon="el-icon-refresh">
                  Restart Encoder 
                </el-dropdown-item>
                <!-- Disconnect All Routes Action (only show when input has connected outputs) -->
                <template v-if="connectedOutputs.length > 0">
                  <el-dropdown-item command="disconnectAll" icon="el-icon-warning" divided>
                    ⚠️ Disconnect ALL Routes ({{ connectedOutputs.length }})
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { map } from 'lodash';
import placeholder from '../../assets/placeholder.js'; // Adjust import based on your project structure
import { mapState } from 'vuex';
export default {
  name: 'RouterInputItem',
  
  props: {
    input: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    selectedInput: {
      type: Object,
      default: null
    },
    selectedOutputs: {
      type: [Array, Object],
      default: null
    },
    activeOutputInput: {
      type: Object,
      default: () => ({})
    },
    outputs: {
      type: Array,
      default: () => []
    },
    inputColor: {
      type: String,
      required: true
    },
    hasPermissions: {
      type: Boolean,
      default: false
    },
    showTakeId: {
      type: [String, Number],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    isRoutingLoading: {
      type: Boolean,
      default: false
    },
    thumbnailData: {
      type: Object,
      default: () => ({
        data: null,
        loading: false,
        error: null,
        warning: null,
        timestamp: null
      })
    }
  },

  data() {
    return {
      showTakeConfirmation: false
    };
  },
  
  watch: {
    selectedOutputs: {
      handler(newValue) {
        // Cancel take confirmation if no outputs are selected
        if (!newValue || newValue.length === 0) {
          this.showTakeConfirmation = false;
        }
      },
      immediate: true
    }
  },
  
  emits: [
    'route-input',
    'show-take',
    'confirm-take',
    'input-operation',
    'disconnect-all-routes'
  ],
  
  computed: {
    ...mapState({
      isSysAdmin: (state) => state.userAccount.isSysAdmin,
    }),
    isInputRouted() {
      return this.outputs.some(output => output.routedInput?.id === this.input.id);
    },
    
    connectedOutputs() {
      return this.outputs.filter(output => output.routedInput?.id === this.input.id);
    },
  },

  methods: {
    formatThumbnailTime(timestamp) {
      if (!timestamp) return 'Never';
      
      const now = new Date();
      const diff = now - timestamp;
      const seconds = Math.floor(diff / 1000);
      
      if (seconds < 60) return `${seconds}s ago`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      return `${Math.floor(seconds / 3600)}h ago`;
    },

    confirmTake() {
      this.showTakeConfirmation = false;
      this.$emit('confirm-take', this.input, this.selectedOutputs);
    },

    cancelTake() {
      this.showTakeConfirmation = false;
    },

    handleMenuCommand(command) {
      if (['restartEncoder', 'startInput', 'stopInput', 'restartInput'].includes(command)) {
        this.$emit('input-operation', this.input, command);
      } else if (command === 'disconnectAll') {
        this.$emit('disconnect-all-routes', this.input);
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles can be added here if needed */
/* Most styles are inherited from parent RouterAlt.vue */

/* Thumbnail preview styling */
.thumbnail-preview {
  transition: all 0.2s ease-in-out;
}

.thumbnail-preview:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Smooth loading animation */
.thumbnail-loading {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
}

/* Error state styling */
.thumbnail-error {
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
}

/* No data state styling */
.thumbnail-no-data {
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  opacity: 0.7;
}

/* Routing loading overlay animation */
.routing-loading {
  animation: routingPulse 1.5s ease-in-out infinite;
  transition: opacity 0.2s ease-in-out;
}

@keyframes routingPulse {
  0% { 
    opacity: 0.8;
    transform: scale(1);
  }
  50% { 
    opacity: 1;
    transform: scale(1.02);
  }
  100% { 
    opacity: 0.8;
    transform: scale(1);
  }
}
</style>
